import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { countersApi, type CounterTableSummary } from '@/api/queries/counters';
import {
  Button,
  Icon,
  IconButton,
  Input,
  Pagination,
  Table,
  useToast,
  type TableColumn,
} from '@/components/ui';

import { EditCounterTableModal } from './EditCounterTableModal';
import { NewCounterTableModal } from './NewCounterTableModal';

export function CountersPage() {
  const search = useSearch({ from: '/counters/' });
  const navigate = useNavigate({ from: '/counters/' });
  const { data: tables = [], isPending } = countersApi.useListTables(search.q);
  const deleteTable = countersApi.useDeleteTable();
  const toast = useToast();
  const [showCreate, setShowCreate] = useState(false);
  const [editTable, setEditTable] = useState<CounterTableSummary | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const setQ = (q: string) => {
    setPage(0);
    navigate({ search: (prev) => ({ ...prev, q: q || undefined }) });
  };

  const pageRows = useMemo(
    () => tables.slice(page * pageSize, page * pageSize + pageSize),
    [tables, page, pageSize],
  );

  const handleDelete = (t: CounterTableSummary) => {
    if (!confirm(`Delete counter table "${t.name}" (counter.${t.key})? This can't be undone.`)) return;
    deleteTable.mutate(t.key, {
      onSuccess: () => toast.show({ severity: 'success', title: `Deleted ${t.name}` }),
      onError: (err) =>
        toast.show({
          severity: 'error',
          title: err instanceof Error ? err.message : 'Failed to delete counter',
        }),
    });
  };

  const columns: TableColumn<CounterTableSummary>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: (t) => (
        <Link to="/counters/$key" params={{ key: t.key }} style={{ color: 'inherit' }}>
          <strong>{t.name}</strong>
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>counter.{t.key}</div>
        </Link>
      ),
    },
    { id: 'rows', header: 'Rows', width: 100, cell: (t) => t.rowCount.toLocaleString() },
    { id: 'fields', header: 'Fields', width: 80, cell: (t) => t.fieldCount },
    { id: 'langs', header: 'Languages', width: 180, cell: (t) => t.langs.join(', ') },
    { id: 'usedIn', header: 'Used in', width: 90, cell: (t) => t.usedIn },
    {
      id: 'updated',
      header: 'Last updated',
      width: 170,
      cell: (t) => new Date(t.updatedAt).toLocaleString(),
    },
  ];

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Counters</h1>
          <div className="page-subtitle">{tables.length} tables</div>
        </div>
        <div className="page-actions">
          <Button
            variant="primary"
            leftIcon={<Icon name="plus" size={14} />}
            onClick={() => setShowCreate(true)}
          >
            New counter table
          </Button>
        </div>
      </header>

      <div className="toolbar" style={{ marginBottom: 14 }}>
        <Input
          placeholder="Search by name, key, description"
          value={search.q ?? ''}
          onChange={(e) => setQ(e.currentTarget.value)}
          style={{ flex: 1, maxWidth: 380 }}
        />
      </div>

      <Table<CounterTableSummary>
        columns={columns}
        rows={pageRows}
        rowKey={(t) => t.id}
        loading={isPending}
        emptyMessage="No counter tables yet."
        rowActions={(t) => (
          <>
            <IconButton
              icon="edit"
              iconSize={13}
              size="sm"
              label="Edit table"
              className="row-action"
              onClick={() => setEditTable(t)}
            />
            <IconButton
              icon="trash"
              iconSize={13}
              size="sm"
              label="Delete table"
              className="row-action danger"
              onClick={() => handleDelete(t)}
            />
          </>
        )}
      />

      <Pagination
        page={page}
        pageSize={pageSize}
        total={tables.length}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(0);
        }}
      />

      {showCreate && <NewCounterTableModal onClose={() => setShowCreate(false)} />}
      {editTable && (
        <EditCounterTableModal table={editTable} onClose={() => setEditTable(null)} />
      )}
    </div>
  );
}
